/**
 * useArrangeModePreview — plays a one-shot "showcase" of every alternative
 * variant on the first entry to Arrange mode for a given product within the
 * current page session.
 *
 * Behaviour:
 *  - Triggers once per page session — tracked in a module-level flag, so
 *    switching products or re-entering Arrange mode never replays it.
 *    A full page reload resets the flag.
 *  - Steps through `s = 1..max(variantsCount)` at a fixed interval, setting
 *    `selectedVariants[i] = s < variantsCount[i] ? s : 0`. The longest
 *    compartment naturally wraps to 0 on the final step → all-zero final state.
 *  - Skipped entirely when no compartment has alternatives (max <= 1) or
 *    while variants are still loading (`enabled === false`).
 *  - Cancelled if any user input arrives via `userInteracted` flag — the
 *    preview gracefully aborts and the user keeps whatever state they set.
 *
 * The hook is intentionally I/O-free: it owns no DOM, no fetches — only a
 * single setTimeout chain wired through context actions.
 *
 * @param {object}   params
 * @param {string|null}      params.productKey      — product.uuid or equivalent stable id
 * @param {number[]}         params.variantsCount   — indexed by (compartmentNumber - 1)
 * @param {boolean}          params.enabled         — gate (e.g., variants finished loading)
 * @param {boolean}          params.userInteracted  — flips true on first hover/click — aborts the run
 * @param {(arr:number[]) => void} params.setAllVariants — bulk setter, one render per tick
 * @param {number}           [params.intervalMs=250]
 */

import { useEffect, useRef } from 'react'

// Module-level flag — flips to true after the first preview plays in this session.
let previewPlayedThisSession = false

// Test-only escape hatch.
export function __clearArrangePreviewMemoryForTests() {
  previewPlayedThisSession = false
}

export function useArrangeModePreview({
  productKey,
  variantsCount,
  enabled,
  userInteracted,
  setAllVariants,
  intervalMs = 400,
}) {
  // Stable refs so the scheduling effect does not re-trigger on every parent re-render.
  const variantsCountRef  = useRef(variantsCount)
  variantsCountRef.current = variantsCount
  const setAllVariantsRef = useRef(setAllVariants)
  setAllVariantsRef.current = setAllVariants
  const userInteractedRef = useRef(userInteracted)
  userInteractedRef.current = userInteracted

  useEffect(() => {
    if (!enabled || !productKey) return
    if (userInteracted) return
    if (previewPlayedThisSession) return

    const counts = variantsCountRef.current
    const numSteps = counts.reduce((m, c) => (c > m ? c : m), 1)
    if (numSteps <= 1) {
      previewPlayedThisSession = true
      return
    }

    previewPlayedThisSession = true

    let cancelled = false
    let timer = null

    const runStep = (s) => {
      if (cancelled || userInteractedRef.current) return
      if (s > numSteps) return

      const next = counts.map((c) => (s < c ? s : 0))
      setAllVariantsRef.current(next)

      timer = window.setTimeout(() => runStep(s + 1), intervalMs)
    }

    timer = window.setTimeout(() => runStep(1), intervalMs)

    return () => {
      cancelled = true
      if (timer != null) window.clearTimeout(timer)
    }
  }, [productKey, enabled, userInteracted, intervalMs])
}
